/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState, ReactElement, useRef, useContext } from "react";
import axios from "axios";
import Card from "components/card";
import { Button, Badge } from "@chakra-ui/react";
import { FaAngleDown, FaAngleLeft } from "react-icons/fa";
import SweetAlert2 from "react-sweetalert2";
import { TokenManager } from "utils/tokenManager";
import BatchPayoutList from "./components/BatchPayoutList";
import BatchDetailErrorInfoModal from "./components/BatchDetailErrorInfoModal";
import {
  MdOutlineCancel,
  MdOutlineCheckCircleOutline,
  MdOutlinePlayCircleOutline,
  MdPersonAddAlt,
  MdOutlineContentCopy,
} from "react-icons/md";
import { Tooltip, IconButton } from "@mui/material";
import { ImCogs } from "react-icons/im";
import { useParams, useNavigate } from "react-router-dom";
import { HiDotsVertical } from "react-icons/hi";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import BatchCancelConfirmModal from "./components/BatchCancelConfirmModal";
import BatchUpdateStatusConfirmModal from "./components/BatchUpdateStatusConfirmModal";
import BatchAssignProviderModal from "./components/BatchAssignProviderModal";
import BatchProcessConfirmModal from "./components/BatchProcessConfirmModal";
import { AiOutlineDownload } from "react-icons/ai";
import { ProfileContext } from "../../../store/ProfileProvider";
import DisplayAmount from "../shared/DisplayAmount";
import { usePageWebSocket } from "../../../hooks/usePageWebSocket";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const rowPerPage = 10;

interface PayloadType {
  [key: string]: any;
}

const BatchDetail = ({ userPermissions }: { userPermissions: any }) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [batchDetailForEdit, setBatchDetailForEdit] = useState(null);
  const [batchProviderList, setBatchProviderList] = useState(null);
  const [swalProps, setSwalProps] = useState<any>({});
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFetchProvider, setIsFetchProvider] = useState<boolean>(true);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const [isOpenBatchDetailErrorInfoModal, setIsOpenBatchDetailErrorInfoModal] =
    useState<boolean>(false);

  const [batchToCancel, setBatchToCancel] = useState<any>(null);
  const [isOpenBatchCancelConfirmModal, setIsOpenBatchCancelConfirmModal] =
    useState<boolean>(false);

  const [batchToUpdateStatus, setBatchToUpdateStatus] = useState<any>(null);
  const [
    isOpenBatchUpdateStatusConfirmModal,
    setIsOpenBatchUpdateStatusConfirmModal,
  ] = useState<boolean>(false);

  const [batchToAssignProvider, setBatchToAssignProvider] = useState<any>(null);
  const [isOpenBatchAssignProviderModal, setIsOpenBatchAssignProviderModal] =
    useState<boolean>(false);

  const [batchToProcessConfirm, setBatchToProcessConfirm] = useState<any>(null);
  const [isOpenBatchProcessConfirmModal, setIsOpenBatchProcessConfirmModal] =
    useState<boolean>(false);

  const [isAsyncProcessing, setIsAsyncProcessing] = useState<boolean>(false);
  const [isActionHidden, setIsActionHidden] = useState(TokenManager.isImpersonating());

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [statusToUpdate, setStatusToUpdate] = useState<string>("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const { received } = usePageWebSocket(`batch_${id}`);

  useEffect(() => {
    const fetchData = async () => {
      await fetchBatchDetail(id);
    };

    if (batchDetailForEdit === null) {
      fetchData();
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []); // eslint-disable-line

  // Listen for WebSocket messages
  useEffect(() => {
    if (!received) return;

    // Handle individual payout cancel — update row status directly
    if (received.messageType === "payout") {
      const payoutId = received.data?.payout_id;
      const newStatus = received.data?.status;
      if (payoutId && newStatus) {
        setTableData((prev: any) => prev.map((row: any) =>
          row.id === payoutId ? { ...row, status: newStatus } : row
        ));
      }
      return;
    }

    if (received.messageType !== "batch") return;

    const isProcessingStarted = !!received.data?.processing;

    if (isProcessingStarted) {
      setIsAsyncProcessing(true);
      // Update status from WS data if provided
      const newStatus = received.data?.batch_status;
      if (newStatus) {
        setBatchDetailForEdit((prev: any) => prev ? { ...prev, status: newStatus } : prev);
      }
      return;
    }

    // SNS complete
    {
      const wasManual = batchDetailForEdit?.is_manual === "true";
      const wasCancelling = batchDetailForEdit?.status === "cancelling";
      const wasSettling = batchDetailForEdit?.status === "settling";
      // Fetch batch detail and payouts in parallel, then update all state at once
      const queryObject: PayloadType = {
        limit: rowPerPage,
        lastkey: "",
        batch_id: id,
        usertype: userRole === "superadmin" ? "a" : "m",
      };
      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      Promise.all([
        axios.get(
          process.env.REACT_APP_API_URL +
            `/api/batch/func/get-batch-by-id?batchid=${id}`
        ),
        axios.get(
          process.env.REACT_APP_API_URL +
            `/api/payout/get-all-payouts?${queryString}`
        ),
      ]).then(([batchRes, payoutsRes]) => {
        const batchData = batchRes.data.batch?.payload;
        const payoutData = payoutsRes.data.payouts?.payload;
        const newLastKey = payoutsRes.data.payouts?.lastkey;

        // Set all state together so React batches into one render
        if (batchData) setBatchDetailForEdit(batchData);
        setTableData(payoutData?.length ? payoutData : []);
        setIsAsyncProcessing(false);
        if (!wasManual || wasCancelling || wasSettling) setIsActionHidden(true);

        // Handle pagination
        if (newLastKey) {
          setCurrentLastKey(newLastKey);
          setRowCount(rowPerPage + rowPerPage);
          setLastKeys({ 0: newLastKey });
        } else {
          setCurrentLastKey("");
        }
        setPage(0);
      }).catch((error) => {
        console.error("Error refreshing after batch process complete", error);
        setIsAsyncProcessing(false);
      });
    }
  }, [received]); // eslint-disable-line

  const handleProcessingStarted = (batchId: string, optimisticStatus?: string) => {
    setIsAsyncProcessing(true);
    // Optimistically update the status in frontend state
    // Process button: manual → initializing, auto → processing
    // Mark In Progress: → processing
    // Cancel: → cancelling
    setBatchDetailForEdit((prev: any) => {
      if (!prev) return prev;
      let status: string;
      if (optimisticStatus === "in_progress") {
        status = "processing";
      } else if (optimisticStatus === "cancelling") {
        status = "cancelling";
      } else if (optimisticStatus === "settled" || optimisticStatus === "failed") {
        status = "settling";
      } else if (optimisticStatus) {
        status = optimisticStatus;
      } else {
        status = prev.is_manual === "true" ? "initializing" : "processing";
      }
      return { ...prev, status };
    });
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsDropdownOpen(false);
    }
  };

  const fetchBatchDetail = async (batchId: string) => {
    try {
      setBatchDetailForEdit(null);
      setIsLoading(true);

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/batch/func/get-batch-by-id?batchid=${batchId}`
      );

      let batchData = response.data.batch?.payload;

      // Check if batch data exists, if not redirect to batch listing
      if (!batchData) {
        console.error("Batch not found");
        navigate(`/${userRole}/batch`);
        return;
      }

      setBatchDetailForEdit(batchData);
      if (["initializing", "processing", "settling", "cancelling"].includes(batchData.status)) {
        setIsAsyncProcessing(true);
      }
      await fetchPayouts({
        page: 0,
        limit: rowPerPage,
        lastkey: "",
        batch_id: id,
      });
      await fetchBatchProvider(String(id));
    } catch (error) {
      setTableData([]);
      setIsLoading(false);
      console.error("Error fetching data", error);
      // Redirect to batch listing page on error
      navigate(`/${userRole}/batch`);
    }
  };

  const fetchBatchProvider = async (id: string) => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/batch/func/get-method?type=batch&entityid=${id}`
      );

      let providerData = response.data.provider;

      setBatchProviderList(providerData);
      setIsFetchProvider(false);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  const fetchPayouts = async (payload: PayloadType = {}, silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const queryObject: PayloadType = {
        limit: payload?.limit,
        lastkey: payload?.lastkey,
        batch_id: payload?.batch_id,
        usertype: userRole === "superadmin" ? "a" : "m",
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/payout/get-all-payouts?${queryString}`
      );

      let newData = response.data.payouts?.payload;

      if (newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }
      setIsLoading(false);

      let newLastKey = response.data.payouts?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        let newPage = payload?.page;
        let newPageSize = payload?.limit;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys({ ...lastKeys, [newPage]: newLastKey });
      } else {
        setCurrentLastKey("");
      }
    } catch (error) {
      setTableData([]);
      setIsLoading(false);
      setPage(0);
      setPageSize(0);
      setRowCount(0);
      setLastKeys([]);
      setCurrentLastKey("");
      console.error("Error fetching data", error);
    }
  };

  const setPaginationModel = (newValue: any) => {
    setIsLoading(true);

    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      batch_id: id,
    };

    if (newValue.page < page) {
      newFilters = {
        ...newFilters,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        batch_id: id,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys([]);
    } else {
      setPage(newValue.page);
    }

    fetchPayouts(newFilters);
  };

  interface BatchStatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: BatchStatusItems[] = [
    {
      status: "open",
      value: "open",
      display: <Badge className={`${statusClasses} status-open`}>Open</Badge>,
    },
    {
      status: "close",
      value: "close",
      display: (
        <Badge className={`${statusClasses} status-closed`}>Closed</Badge>
      ),
    },
    {
      status: "queue",
      value: "queue",
      display: (
        <Badge className={`${statusClasses} status-queued`}>Queued</Badge>
      ),
    },
    {
      status: "initializing",
      value: "initializing",
      display: (
        <Badge className={`${statusClasses} status-initializing`}>Initializing</Badge>
      ),
    },
    {
      status: "processing",
      value: "processing",
      display: (
        <Badge className={`${statusClasses} status-processing`}>Processing</Badge>
      ),
    },
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "in_progress",
      value: "in_progress",
      display: (
        <Badge className={`${statusClasses} status-in_progress`}>
          In Progress
        </Badge>
      ),
    },
    {
      status: "settled",
      value: "settled",
      display: (
        <Badge className={`${statusClasses} status-settled`}>Settled</Badge>
      ),
    },
    {
      status: "settled_with_error",
      value: "settled_with_error",
      display: (
        <Badge className={`${statusClasses} status-settled_with_error`}>
          Settled with Errors
        </Badge>
      ),
    },
    {
      status: "settling",
      value: "settling",
      display: (
        <Badge className={`${statusClasses} status-settling`}>Settling</Badge>
      ),
    },
    {
      status: "cancelling",
      value: "cancelling",
      display: (
        <Badge className={`${statusClasses} status-cancelling`}>Cancelling</Badge>
      ),
    },
    {
      status: "cancelled",
      value: "cancelled",
      display: (
        <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge>
      ),
    },
    {
      status: "failed",
      value: "failed",
      display: (
        <Badge className={`${statusClasses} status-failed`}>Failed</Badge>
      ),
    },
  ];

  const statusDisplay = (status: string) => {
    const statusItem = statusList.find((item) => item.status === status);
    return statusItem ? statusItem.display : null;
  };

  const formatDate = (timestamp: string, time: boolean = false) => {
    if (timestamp && timestamp !== "") {
      if (time) {
        return dayjs(Number(timestamp))
          .tz(profileTimeZone)
          .format("DD MMM YY - hh:mm A");
      } else {
        return dayjs(Number(timestamp)).tz(profileTimeZone).format("DD MMM YY");
      }
    }
    return "-";
  };

  const showErrorInfoModal = () => {
    setIsOpenBatchDetailErrorInfoModal(true);
  };

  const closeErrorInfoModal = () => {
    setIsOpenBatchDetailErrorInfoModal(false);
  };

  const handleClickBack = () => {
    navigate(`/${userRole}/batch`);
  };

  const handleClickCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const handleClickCancel = () => {
    setBatchToCancel(batchDetailForEdit);
    showBatchCancelConfirmModal();
  };

  const showBatchCancelConfirmModal = () => {
    setIsOpenBatchCancelConfirmModal(true);
  };

  const closeBatchCancelConfirmModal = () => {
    setBatchToCancel(null);
    setIsOpenBatchCancelConfirmModal(false);
  };

  const handleClickUpdateStatus = (status: string) => {
    setIsDropdownOpen(false);
    setStatusToUpdate(status);
    setBatchToUpdateStatus(batchDetailForEdit);
    showBatchUpdateStatusConfirmModal();
  };

  const showBatchUpdateStatusConfirmModal = () => {
    setIsOpenBatchUpdateStatusConfirmModal(true);
  };

  const closeBatchUpdateStatusConfirmModal = () => {
    setBatchToUpdateStatus(null);
    setIsOpenBatchUpdateStatusConfirmModal(false);
  };

  const handleClickAssignProvider = () => {
    setBatchToAssignProvider(batchDetailForEdit);
    showBatchAssignProviderModal();
  };

  const showBatchAssignProviderModal = () => {
    setIsOpenBatchAssignProviderModal(true);
  };

  const closeBatchAssignProviderModal = () => {
    setBatchToAssignProvider(null);
    setIsOpenBatchAssignProviderModal(false);
  };

  const handleClickProcessConfirm = () => {
    setBatchToProcessConfirm(batchDetailForEdit);
    showBatchProcessConfirmModal();
  };

  const showBatchProcessConfirmModal = () => {
    setIsOpenBatchProcessConfirmModal(true);
  };

  const closeBatchProcessConfirmModal = () => {
    setBatchToProcessConfirm(null);
    setIsOpenBatchProcessConfirmModal(false);
  };

  const triggerReloadBatchDetail = async () => {
    setIsLoading(true);
    await fetchBatchDetail(id);
  };

  const triggerReloadAfterStatusUpdate = async () => {
    setIsActionHidden(true);
    setIsLoading(true);
    await fetchBatchDetail(id);
    setIsActionHidden(false);
  };

  const refreshBatchDetailSilent = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/batch/func/get-batch-by-id?batchid=${id}`
      );
      let batchData = response.data.batch?.payload;
      if (batchData) {
        setBatchDetailForEdit(batchData);
      }
    } catch (error) {
      console.error("Error refreshing batch detail", error);
    }
  };

  const triggerReloadPayoutList = async () => {
    setIsLoading(true);
    await refreshBatchDetailSilent();
    await fetchPayouts({
      page: 0,
      limit: rowPerPage,
      lastkey: "",
      batch_id: batchDetailForEdit.id,
    });
  };

  const handleClickDownload = async (
    file_bucket: string,
    file_key: string,
    file_name: string
  ) => {
    try {
      setIsDownloading(true);

      const postData = {
        file_bucket: file_bucket,
        file_key: file_key,
      };

      await axios
        .post(
          process.env.REACT_APP_API_URL +
            `/api/${userRole}/batch/action/batch-generate-download-url`,
          postData
        )
        .then(async function (response) {
          setIsDownloading(false);
          if (response && response.data?.status === "ok") {
            const fileUrl = response.data.url;
            const fileResponse = await fetch(fileUrl);
            const blob = await fileResponse.blob();

            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = file_name;
            link.click();

            window.URL.revokeObjectURL(link.href);
          } else {
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: response.data.message,
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
              },
            });
          }
        })
        .catch(function (error) {
          setSwalProps({
            show: true,
            icon: "error",
            title: "Oops!",
            html: error.response.data.message,
            showConfirmButton: true,
            didClose: () => {
              setSwalProps({});
            },
          });
        });
    } catch (error) {
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: "Something went wrong, please try again later.",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
        },
      });
    }
  };

  return (
    <>
      {isOpenBatchCancelConfirmModal && (
        <BatchCancelConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadBatchDetail}
          onProcessingStarted={handleProcessingStarted}
          isOpen={isOpenBatchCancelConfirmModal}
          batchToCancel={batchToCancel}
          onClose={closeBatchCancelConfirmModal}
        />
      )}

      {userRole === "superadmin" && (
        <>
          {isOpenBatchAssignProviderModal && (
            <BatchAssignProviderModal
              setSwalProps={setSwalProps}
              triggerReload={triggerReloadBatchDetail}
              isOpen={isOpenBatchAssignProviderModal}
              batchToAssignProvider={batchToAssignProvider}
              batchProviderList={batchProviderList}
              onClose={closeBatchAssignProviderModal}
            />
          )}
          {isOpenBatchProcessConfirmModal && (
            <BatchProcessConfirmModal
              setSwalProps={setSwalProps}
              triggerReload={triggerReloadBatchDetail}
              onProcessingStarted={handleProcessingStarted}
              isOpen={isOpenBatchProcessConfirmModal}
              batchToProcessConfirm={batchToProcessConfirm}
              onClose={closeBatchProcessConfirmModal}
            />
          )}
          {isOpenBatchUpdateStatusConfirmModal && (
            <BatchUpdateStatusConfirmModal
              setSwalProps={setSwalProps}
              statusToUpdate={statusToUpdate}
              triggerReload={triggerReloadAfterStatusUpdate}
              onProcessingStarted={handleProcessingStarted}
              isOpen={isOpenBatchUpdateStatusConfirmModal}
              batchToUpdateStatus={batchToUpdateStatus}
              onClose={closeBatchUpdateStatusConfirmModal}
            />
          )}
        </>
      )}

      {isOpenBatchDetailErrorInfoModal &&
        batchDetailForEdit !== null &&
        batchDetailForEdit.validation_results && (
          <BatchDetailErrorInfoModal
            data={batchDetailForEdit.validation_results}
            isOpen={isOpenBatchDetailErrorInfoModal}
            onClose={closeErrorInfoModal}
          />
        )}

      <Card className="card card-pad !rounded-xl">
        {batchDetailForEdit !== null ? (
          <>
            <div className="mb-3 flex items-center">
              <div>
                <button
                  type="button"
                  onClick={handleClickBack}
                  disabled={isLoading || isDownloading}
                  className="
                    mb-1 inline-flex items-center gap-1
                    text-sm font-medium text-gray-700
                    transition hover:text-gray-700
                    disabled:cursor-not-allowed disabled:opacity-40
                    dark:text-gray-400 dark:hover:text-white
                  "
                >
                  <FaAngleLeft className="text-base" />
                  <span>Batches</span>
                </button>

                <h3 className="text-xl font-semibold text-navy-700">
                  Batch Info
                </h3>
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  ID: {batchDetailForEdit?.id || "-"}
                  <Tooltip title="Copy Batch ID" arrow placement="top">
                    <IconButton
                      size="small"
                      onClick={() => handleClickCopy(batchDetailForEdit?.id)}
                    >
                      <MdOutlineContentCopy className="h-3.5 w-3.5 text-gray-500" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>

              <div className="ml-auto flex items-center">
                {/* old code */}
                {/* <div className="flex">
                  {batchDetailForEdit.status === "open" &&
                    userRole !== "superadmin" &&
                    userPermissions?.cancelBatch && (
                      <>
                        <span className="mx-3 inline-block h-8 border-l border-gray-200"></span>
                        <Button
                          isDisabled={batchDetailForEdit === null || isLoading}
                          onClick={() => handleClickCancel()}
                          className="btn rounded-xl bg-red-600 text-sm font-medium text-white transition duration-200 hover:bg-red-500 hover:text-white active:bg-red-700 dark:border-red-400 dark:text-red-400 dark:active:bg-red-200"
                        >
                          Cancel Batch
                        </Button>
                      </>
                    )}
                  {batchDetailForEdit.status === "in_progress" &&
                    batchDetailForEdit.is_manual === "false" &&
                    batchDetailForEdit.is_can_process_again === "true" &&
                    userRole === "superadmin" && (
                      <>
                        <span className="mx-3 inline-block h-8 border-l border-gray-200"></span>
                        <Button
                          isDisabled={batchDetailForEdit === null || isLoading}
                          onClick={() => handleClickProcessConfirm()}
                          className="btn rounded-xl border border-amber-500 text-sm font-medium text-amber-500 transition duration-200 hover:bg-amber-500 hover:text-white active:bg-amber-700 dark:border-amber-400 dark:text-amber-400 dark:active:bg-amber-200"
                        >
                          <MdOutlinePlayCircleOutline className="mr-2 text-base" />
                          Process
                        </Button>
                      </>
                    )}
                  {(["open", "close", "queue"].includes(
                    batchDetailForEdit.status
                  ) ||
                    (batchDetailForEdit.status === "in_progress" &&
                      batchDetailForEdit.is_manual === "true")) &&
                    userRole === "superadmin" && (
                      <>
                        <span className="mx-3 inline-block h-8 border-l border-gray-200"></span>
                        <div ref={dropdownRef}>
                          <Button
                            onClick={toggleDropdown}
                            isDisabled={
                              batchDetailForEdit === null ||
                              isLoading ||
                              isDownloading ||
                              isFetchProvider
                            }
                            className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {(batchDetailForEdit === null ||
                              isLoading ||
                              isFetchProvider) && <>Preparing...</>}
                            {batchDetailForEdit &&
                              !isLoading &&
                              !isFetchProvider && (
                                <>
                                  Action&nbsp;
                                  <FaAngleDown />
                                </>
                              )}
                          </Button>
                          {isDropdownOpen && (
                            <div className="absolute mt-2 origin-top-right rounded-md border border-gray-300 bg-white shadow-lg">
                              <div
                                className="p-2"
                                role="menu"
                                aria-orientation="vertical"
                                aria-labelledby="options-menu"
                              >
                                {userRole === "superadmin" &&
                                  (batchDetailForEdit.status === "open" ||
                                    batchDetailForEdit.status === "close") && (
                                    <span
                                      onClick={() =>
                                        handleClickAssignProvider()
                                      }
                                      className="flex cursor-pointer items-center px-2 py-2 text-sm font-medium text-navy-500 hover:bg-navy-100"
                                    >
                                      <ImCogs className="mr-2 text-base" />
                                      Assign Provider
                                    </span>
                                  )}
                                {userRole === "superadmin" &&
                                  batchDetailForEdit.status === "close" && (
                                    <span
                                      onClick={() =>
                                        handleClickProcessConfirm()
                                      }
                                      className="flex cursor-pointer items-center px-2 py-2 text-sm font-medium text-amber-500 hover:bg-amber-100"
                                    >
                                      <MdOutlinePlayCircleOutline className="mr-2 text-base" />
                                      Process
                                    </span>
                                  )}
                                {userRole === "superadmin" &&
                                  batchDetailForEdit.status === "queue" &&
                                  batchDetailForEdit.file_bucket !== null &&
                                  batchDetailForEdit.file_key !== null && (
                                    <span
                                      onClick={() =>
                                        handleClickUpdateStatus("in_progress")
                                      }
                                      className="flex cursor-pointer items-center px-2 py-2 text-sm font-medium text-amber-500 hover:bg-amber-100"
                                    >
                                      <MdOutlineCheckCircleOutline className="mr-2 h-4 w-4 shrink-0" />
                                      Mark Batch as In Progress
                                    </span>
                                  )}
                                {(userRole === "superadmin" &&
                                  (batchDetailForEdit.status === "open" ||
                                    batchDetailForEdit.status === "close" ||
                                    batchDetailForEdit.status === "queue")) ||
                                  (userRole === "merchant" &&
                                    batchDetailForEdit.status === "open" &&
                                    userPermissions?.cancelBatch) ? (
                                    <span
                                      onClick={() => handleClickCancel()}
                                      className="flex cursor-pointer items-center px-2 py-2 text-sm font-medium text-red-500 hover:bg-red-100"
                                    >
                                      <MdOutlineCancel className="mr-2 h-4 w-4 shrink-0" />
                                      Cancel
                                    </span>
                                  ) : null}
                                {userRole === "superadmin" &&
                                  batchDetailForEdit.status === "in_progress" &&
                                  batchDetailForEdit.is_manual === "true" && (
                                    <span
                                      onClick={() =>
                                        handleClickUpdateStatus("settled")
                                      }
                                      className="flex cursor-pointer items-center px-2 py-2 text-sm font-medium text-green-500 hover:bg-green-100"
                                    >
                                      <MdOutlineCheckCircleOutline className="mr-2 h-4 w-4 shrink-0" />
                                      Mark Batch as Settled
                                    </span>
                                  )}
                                {userRole === "superadmin" &&
                                  batchDetailForEdit.status === "in_progress" &&
                                  batchDetailForEdit.is_manual === "true" && (
                                    <span
                                      onClick={() =>
                                        handleClickUpdateStatus("failed")
                                      }
                                      className="flex cursor-pointer items-center px-2 py-2 text-sm font-medium text-red-500 hover:bg-red-100"
                                    >
                                      <MdOutlineCancel className="mr-2 h-4 w-4 shrink-0" />
                                      Mark Batch as Failed
                                    </span>
                                  )}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                </div> */}

                <div className="flex items-center">
                  {batchDetailForEdit.status === "open" &&
                    userRole !== "superadmin" &&
                    userPermissions?.cancelBatch && (
                      <>
                        <span className="mx-3 inline-block h-8 border-l border-gray-200" />
                        <Button
                          isDisabled={batchDetailForEdit === null || isLoading}
                          onClick={() => handleClickCancel()}
                          className="btn rounded-xl bg-red-600 text-sm font-medium text-white transition duration-200 hover:bg-red-500 hover:text-white active:bg-red-700"
                        >
                          Cancel Batch
                        </Button>
                      </>
                    )}

                  {batchDetailForEdit.status === "in_progress" &&
                    batchDetailForEdit.is_manual === "false" &&
                    batchDetailForEdit.is_can_process_again === "true" &&
                    !isAsyncProcessing &&
                    userRole === "superadmin" && (
                      <>
                        <Button
                          isDisabled={batchDetailForEdit === null || isLoading}
                          onClick={() => handleClickProcessConfirm()}
                          className="btn rounded-xl border border-amber-500 text-sm font-medium text-amber-500 transition duration-200 hover:bg-amber-500 hover:text-white active:bg-amber-700"
                        >
                          <MdOutlinePlayCircleOutline className="mr-2 text-base" />
                          Process
                        </Button>
                      </>
                    )}

                  {(["open", "close", "queue"].includes(
                    batchDetailForEdit.status
                  ) ||
                    (batchDetailForEdit.status === "in_progress" &&
                      batchDetailForEdit.is_manual === "true")) &&
                    !isAsyncProcessing &&
                    userRole === "superadmin" && (
                      <>
                        {batchDetailForEdit.status === "close" ? (
                          <Button
                            isDisabled={
                              batchDetailForEdit === null ||
                              isLoading ||
                              isFetchProvider
                            }
                            onClick={() => handleClickProcessConfirm()}
                            className="btn rounded-xl border border-amber-500 text-sm font-medium text-amber-500 transition duration-200 hover:bg-amber-500 hover:text-white active:bg-amber-700"
                          >
                            <MdOutlinePlayCircleOutline className="mr-2 text-base" />
                            Process
                          </Button>
                        ) : batchDetailForEdit.status === "queue" &&
                          batchDetailForEdit.file_bucket !== null &&
                          batchDetailForEdit.file_key !== null ? (
                          <Button
                            isDisabled={
                              batchDetailForEdit === null ||
                              isLoading ||
                              isFetchProvider ||
                              isAsyncProcessing ||
                              isActionHidden
                            }
                            onClick={() =>
                              handleClickUpdateStatus("in_progress")
                            }
                            className="btn rounded-xl border border-amber-500 text-sm font-medium text-amber-500 transition duration-200 hover:bg-amber-500 hover:text-white active:bg-amber-700"
                          >
                            <MdOutlineCheckCircleOutline className="mr-2 h-5 w-5 shrink-0" />
                            Mark In Progress
                          </Button>
                        ) : batchDetailForEdit.status === "in_progress" &&
                          batchDetailForEdit.is_manual === "true" ? (
                          <Button
                            isDisabled={
                              batchDetailForEdit === null ||
                              isLoading ||
                              isFetchProvider ||
                              isAsyncProcessing ||
                              isActionHidden
                            }
                            onClick={() => handleClickUpdateStatus("settled")}
                            className="btn rounded-xl border border-green-500 text-sm font-medium text-green-600 transition duration-200 hover:bg-green-500 hover:text-white active:bg-green-700"
                          >
                            <MdOutlineCheckCircleOutline className="mr-2 h-5 w-5 shrink-0" />
                            Mark Settled
                          </Button>
                        ) : (
                          <Button
                            isDisabled={
                              batchDetailForEdit === null ||
                              isLoading ||
                              isDownloading ||
                              isFetchProvider ||
                              isAsyncProcessing
                            }
                            onClick={() => handleClickAssignProvider()}
                            className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <ImCogs className="mr-2 text-lg" />
                            Assign Provider
                          </Button>
                        )}

                        <div ref={dropdownRef} className="relative ml-2">
                          <Button
                            onClick={toggleDropdown}
                            isDisabled={
                              batchDetailForEdit === null ||
                              isLoading ||
                              isDownloading ||
                              isFetchProvider ||
                              isAsyncProcessing ||
                              isActionHidden
                            }
                            className="
                              bg-transparent
                              relative flex h-9 w-9 items-center
                              justify-center
                              rounded-full
                              text-gray-500
                              transition
                              hover:bg-gray-100 hover:text-gray-700
                              active:bg-gray-200
                              disabled:cursor-not-allowed disabled:opacity-40
                              dark:text-gray-400
                              dark:hover:bg-white/10 dark:hover:text-white
                            "
                            aria-label="More actions"
                          >
                            <HiDotsVertical className="h-5 w-5" />
                          </Button>

                          {isDropdownOpen && (
                            <div className="absolute right-0 mt-2 w-48 origin-top-right rounded-md border border-gray-300 bg-white shadow-lg">
                              <div
                                className="p-2"
                                role="menu"
                                aria-orientation="vertical"
                              >
                                {(batchDetailForEdit.status === "open" ||
                                  batchDetailForEdit.status === "close" ||
                                  batchDetailForEdit.status === "queue") && (
                                  <span
                                    onClick={() => handleClickCancel()}
                                    className="flex cursor-pointer items-center rounded-md px-2 py-2 text-sm font-medium text-red-500 hover:bg-red-100"
                                  >
                                    <MdOutlineCancel className="mr-2 h-4 w-4 shrink-0" />
                                    Cancel
                                  </span>
                                )}

                                {batchDetailForEdit.status === "in_progress" &&
                                  batchDetailForEdit.is_manual === "true" && (
                                    <span
                                      onClick={() =>
                                        handleClickUpdateStatus("failed")
                                      }
                                      className="flex cursor-pointer items-center rounded-md px-2 py-2 text-sm font-medium text-red-500 hover:bg-red-100 dark:hover:bg-red-500/10"
                                    >
                                      <MdOutlineCancel className="mr-2 h-4 w-4 shrink-0" />
                                      Mark Batch as Failed
                                    </span>
                                  )}

                                {(batchDetailForEdit.status === "close" ||
                                  (batchDetailForEdit.status === "queue" &&
                                    batchDetailForEdit.is_manual !== "true")) && (
                                    <span
                                      onClick={() =>
                                        handleClickAssignProvider()
                                      }
                                      className="flex cursor-pointer items-center rounded-md px-2 py-2 text-sm font-medium text-navy-500 hover:bg-navy-50"
                                    >
                                      <ImCogs className="mr-2 text-lg" />
                                      Assign Provider
                                    </span>
                                  )}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                </div>
              </div>
            </div>
            <hr className="mb-8" />

            {(isAsyncProcessing || ["initializing", "processing", "settling", "cancelling"].includes(batchDetailForEdit?.status)) && (
              <div className={`mb-6 flex items-center gap-3 rounded-xl border px-4 py-3 text-sm ${
                batchDetailForEdit?.status === "cancelling"
                  ? "border-gray-500 bg-gray-100 text-gray-800 dark:border-gray-500 dark:bg-gray-600/40 dark:text-gray-200"
                  : batchDetailForEdit?.status === "settling"
                    ? "border-yellow-200 bg-yellow-50 text-yellow-800 dark:border-yellow-900/40 dark:bg-yellow-900/20 dark:text-yellow-300"
                    : "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300"
              }`}>
                <svg className={`h-5 w-5 shrink-0 animate-spin ${
                  batchDetailForEdit?.status === "cancelling"
                    ? "text-gray-600 dark:text-gray-300"
                    : batchDetailForEdit?.status === "settling"
                      ? "text-yellow-600 dark:text-yellow-400"
                      : "text-amber-600 dark:text-amber-400"
                }`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>
                  {batchDetailForEdit?.status === "cancelling"
                    ? "Batch is being cancelled. You'll see the results here shortly."
                    : batchDetailForEdit?.status === "settling"
                      ? "Batch is being settled. You'll see the results here shortly."
                      : batchDetailForEdit?.status === "initializing"
                        ? "Batch is initializing. Payouts are being queued for processing."
                        : "Batch is being processed. You'll see the results here shortly."}
                </span>
              </div>
            )}

            {/* Desktop: 2-column grid */}
            <div className="hidden gap-3 px-1 text-sm md:grid md:grid-cols-2">
              {userRole === "superadmin" && batchDetailForEdit?.merchant && (
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Merchant</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {batchDetailForEdit.merchant.name}
                  </div>
                </div>
              )}
              {userRole === "superadmin" && batchDetailForEdit?.subaccount && (
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Subaccount</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {batchDetailForEdit.subaccount.name}
                  </div>
                </div>
              )}
              <div className="flex">
                <div className="w-32 shrink-0 text-navy-700 dark:text-white">Status</div>
                <div className="font-semibold text-navy-700 dark:text-white">
                  {batchDetailForEdit?.status ? statusDisplay(batchDetailForEdit.status) : "-"}
                </div>
              </div>
              <div className="flex">
                <div className="w-32 shrink-0 text-navy-700 dark:text-white">Created</div>
                <div className="font-semibold text-navy-700 dark:text-white">
                  {batchDetailForEdit?.created_at ? formatDate(batchDetailForEdit.created_at) : "-"}
                </div>
              </div>
              <div className="flex">
                <div className="w-32 shrink-0 text-navy-700 dark:text-white">Reference</div>
                <div className="font-semibold break-all leading-snug text-navy-700 dark:text-white">
                  {batchDetailForEdit?.batch_reference || "-"}
                </div>
              </div>
              <div className="flex">
                <div className="w-32 shrink-0 text-navy-700 dark:text-white">Processed At</div>
                <div className="font-semibold text-navy-700 dark:text-white">
                  {batchDetailForEdit?.processed_at ? formatDate(batchDetailForEdit.processed_at, true) : "-"}
                </div>
              </div>
              <div className="flex">
                <div className="w-32 shrink-0 text-navy-700 dark:text-white">Total Amount</div>
                <div className="font-semibold text-navy-700 dark:text-white">
                  {batchDetailForEdit?.amount ? (
                    batchDetailForEdit.crypto ? batchDetailForEdit.amount : (
                      <DisplayAmount amount={Number(batchDetailForEdit.amount)} currency={batchDetailForEdit.asset} />
                    )
                  ) : "-"}
                </div>
              </div>
              <div className="flex">
                <div className="w-32 shrink-0 text-navy-700 dark:text-white">Asset</div>
                <div className="font-semibold text-navy-700 dark:text-white">
                  {batchDetailForEdit?.asset || "-"}
                </div>
              </div>
              <div className="flex">
                <div className="w-32 shrink-0 text-navy-700 dark:text-white">Total Records</div>
                <div className="font-semibold text-navy-700 dark:text-white">
                  {batchDetailForEdit?.record || "-"}
                </div>
              </div>
              {userRole === "superadmin" && batchDetailForEdit?.provider_name && batchDetailForEdit?.provider_account_name && (
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Provider</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {`${batchDetailForEdit.provider_name} (${batchDetailForEdit.provider_account_name})`}
                  </div>
                </div>
              )}
              <div className="flex">
                <div className="w-32 shrink-0 text-navy-700 dark:text-white">Processed</div>
                <div className="font-semibold text-navy-700 dark:text-white">
                  {batchDetailForEdit?.total_tx && batchDetailForEdit?.status !== "queue" ? batchDetailForEdit.total_tx : "-"}
                </div>
              </div>
              {userRole === "superadmin" && batchDetailForEdit?.provider_method_name && (
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Method</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {batchDetailForEdit.provider_method_name}
                  </div>
                </div>
              )}
              {userRole === "superadmin" && batchDetailForEdit?.file_bucket && batchDetailForEdit?.file_key && (
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">File</div>
                  <div className="flex flex-wrap items-center gap-2 font-semibold text-navy-700 dark:text-white">
                    <span className="break-all">{batchDetailForEdit.file_name}</span>
                    <Button
                      isDisabled={isDownloading}
                      onClick={() => handleClickDownload(batchDetailForEdit.file_bucket, batchDetailForEdit.file_key, batchDetailForEdit.file_name)}
                      className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-cyan-500 px-2.5 py-1 text-xs text-white hover:bg-cyan-600 active:bg-cyan-700 dark:bg-cyan-400 dark:hover:bg-cyan-300"
                    >
                      Download <AiOutlineDownload className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              {batchDetailForEdit?.validation_results?.some((x: any) => !x.isValid) && (
                <div className="flex">
                  <div className="w-32 shrink-0 text-navy-700 dark:text-white">Error Info</div>
                  <div>
                    <Button
                      isDisabled={!batchDetailForEdit}
                      onClick={showErrorInfoModal}
                      className="flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-100 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                    >
                      View Errors
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile: grouped layout */}
            <div className="flex flex-col gap-5 px-1 text-sm md:hidden">
              {/* Identity */}
              {userRole === "superadmin" && batchDetailForEdit?.merchant && (
                <div className="flex flex-col gap-3">
                  <div className="flex">
                    <div className="w-28 shrink-0 text-navy-700 dark:text-white">Merchant</div>
                    <div className="font-semibold text-navy-700 dark:text-white">
                      {batchDetailForEdit.merchant.name}
                    </div>
                  </div>
                  {batchDetailForEdit?.subaccount && (
                    <div className="flex">
                      <div className="w-28 shrink-0 text-navy-700 dark:text-white">Subaccount</div>
                      <div className="font-semibold text-navy-700 dark:text-white">
                        {batchDetailForEdit.subaccount.name}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Status & Dates */}
              <div className="flex flex-col gap-3">
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Status</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {batchDetailForEdit?.status ? statusDisplay(batchDetailForEdit.status) : "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Created</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {batchDetailForEdit?.created_at ? formatDate(batchDetailForEdit.created_at) : "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Processed At</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {batchDetailForEdit?.processed_at ? formatDate(batchDetailForEdit.processed_at, true) : "-"}
                  </div>
                </div>
              </div>

              {/* Reference */}
              <div className="flex flex-col gap-3">
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Reference</div>
                  <div className="font-semibold break-all leading-snug text-navy-700 dark:text-white">
                    {batchDetailForEdit?.batch_reference || "-"}
                  </div>
                </div>
              </div>

              {/* Amount & Records */}
              <div className="flex flex-col gap-3">
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Total Amount</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {batchDetailForEdit?.amount ? (
                      batchDetailForEdit.crypto ? batchDetailForEdit.amount : (
                        <DisplayAmount amount={Number(batchDetailForEdit.amount)} currency={batchDetailForEdit.asset} />
                      )
                    ) : "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Asset</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {batchDetailForEdit?.asset || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Total Records</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {batchDetailForEdit?.record || "-"}
                  </div>
                </div>
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Processed</div>
                  <div className="font-semibold text-navy-700 dark:text-white">
                    {batchDetailForEdit?.total_tx && batchDetailForEdit?.status !== "queue" ? batchDetailForEdit.total_tx : "-"}
                  </div>
                </div>
              </div>

              {/* Provider (SuperAdmin only) */}
              {userRole === "superadmin" && batchDetailForEdit?.provider_name && batchDetailForEdit?.provider_account_name && (
                <div className="flex flex-col gap-3">
                  <div className="flex">
                    <div className="w-28 shrink-0 text-navy-700 dark:text-white">Provider</div>
                    <div className="font-semibold text-navy-700 dark:text-white">
                      {`${batchDetailForEdit.provider_name} (${batchDetailForEdit.provider_account_name})`}
                    </div>
                  </div>
                  {batchDetailForEdit?.provider_method_name && (
                    <div className="flex">
                      <div className="w-28 shrink-0 text-navy-700 dark:text-white">Method</div>
                      <div className="font-semibold text-navy-700 dark:text-white">
                        {batchDetailForEdit.provider_method_name}
                      </div>
                    </div>
                  )}
                  {batchDetailForEdit?.file_bucket && batchDetailForEdit?.file_key && (
                    <div className="flex">
                      <div className="w-28 shrink-0 text-navy-700 dark:text-white">File</div>
                      <div className="flex flex-wrap items-center gap-2 font-semibold text-navy-700 dark:text-white">
                        <span className="break-all">{batchDetailForEdit.file_name}</span>
                        <Button
                          isDisabled={isDownloading}
                          onClick={() => handleClickDownload(batchDetailForEdit.file_bucket, batchDetailForEdit.file_key, batchDetailForEdit.file_name)}
                          className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-cyan-500 px-2.5 py-1 text-xs text-white hover:bg-cyan-600 active:bg-cyan-700 dark:bg-cyan-400 dark:hover:bg-cyan-300"
                        >
                          Download <AiOutlineDownload className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Error Info */}
              {batchDetailForEdit?.validation_results?.some((x: any) => !x.isValid) && (
                <div className="flex">
                  <div className="w-28 shrink-0 text-navy-700 dark:text-white">Error Info</div>
                  <div>
                    <Button
                      isDisabled={!batchDetailForEdit}
                      onClick={showErrorInfoModal}
                      className="flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-100 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                    >
                      View Errors
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div
              className="mb-3 mt-3 h-5 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            ></div>
            <div
              className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            ></div>
            <div
              className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            ></div>
          </>
        )}
      </Card>

      {batchDetailForEdit &&
        (batchDetailForEdit.status !== "queue" ||
          batchDetailForEdit.batch_type === "API") && (
          <Card className="card mt-3">
            <BatchPayoutList
              userPermissions={userPermissions}
              isLoading={isLoading}
              page={page}
              pageSize={pageSize}
              rowCount={rowCount}
              tableData={isLoading ? [] : tableData}
              setPaginationModel={setPaginationModel}
              currentLastKey={currentLastKey}
              triggerReloadPayoutList={triggerReloadPayoutList}
              batchType={batchDetailForEdit.batch_type}
              batchFileBucket={batchDetailForEdit.file_bucket}
              batchFileKey={batchDetailForEdit.file_key}
              batchStatus={batchDetailForEdit.status}
              isAsyncProcessing={isAsyncProcessing}
            />
          </Card>
        )}

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default BatchDetail;
